import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

const FORECAST_MONTHS = 24
const DEFAULT_STAGE = { from: 1, to: 12, value: 15 }

export default class extends Controller {
  static targets = [
    "initialUsers",
    "behavior",
    "model",
    "growthValue",
    "unitLabel",
    "fixedPanel",
    "stagedPanel",
    "fixedHint",
    "stagesList",
    "stageTemplate",
    "stageRow",
    "stageFrom",
    "stageTo",
    "stageValue",
    "canvas",
    "startLabel",
    "month12",
    "month24"
  ]

  connect() {
    this.onModelChange()
    this.onBehaviorChange()
    this.lockFromFields()
    this.syncStageChain()
    this.recalculate()
  }

  disconnect() {
    this.destroyChart()
  }

  onBehaviorChange() {
    const unit = this.behaviorValue() === "linear" ? "польз." : "%"
    this.unitLabelTargets.forEach((el) => {
      el.textContent = unit
    })
    this.updateFixedHint()
    this.recalculate()
  }

  onModelChange() {
    const staged = this.modelValue() === "staged"
    this.fixedPanelTarget.classList.toggle("d-none", staged)
    this.stagedPanelTarget.classList.toggle("d-none", !staged)

    if (staged && this.stageRowTargets.length === 0) {
      this.addStage(null, DEFAULT_STAGE)
    }

    this.stageRowTargets.forEach((row) => {
      row.querySelectorAll("input").forEach((input) => {
        const isFrom = input.matches("[data-growth-form-target='stageFrom']")
        if (isFrom) {
          input.readOnly = true
          input.disabled = !staged
        } else {
          input.disabled = !staged
        }
      })
    })

    if (this.hasGrowthValueTarget) {
      this.growthValueTarget.disabled = staged
    }

    this.lockFromFields()
    this.recalculate()
  }

  addStage(event, preset = null) {
    event?.preventDefault()
    if (!this.hasStageTemplateTarget || !this.hasStagesListTarget) return

    const lastTo = this.lastStageTo()
    const node = this.stageTemplateTarget.content.cloneNode(true)
    const row = node.querySelector("[data-growth-form-target='stageRow']")
    const fromInput = row.querySelector("[data-growth-form-target='stageFrom']")
    const toInput = row.querySelector("[data-growth-form-target='stageTo']")
    const valueInput = row.querySelector("[data-growth-form-target='stageValue']")

    if (preset) {
      if (fromInput) fromInput.value = preset.from
      if (toInput) toInput.value = preset.to
      if (valueInput) valueInput.value = preset.value
    } else {
      const nextFrom = lastTo > 0 ? lastTo + 1 : 1
      if (fromInput) fromInput.value = nextFrom
      if (toInput) {
        toInput.value = ""
        toInput.min = nextFrom
      }
      if (valueInput) valueInput.value = ""
    }

    if (fromInput) fromInput.readOnly = true

    this.stagesListTarget.appendChild(node)
    this.lockFromFields()
    this.onBehaviorChange()
    toInput?.focus()
  }

  removeStage(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-growth-form-target='stageRow']")
    if (!row) return

    if (this.stageRowTargets.length <= 1) {
      const fromInput = row.querySelector("[data-growth-form-target='stageFrom']")
      const toInput = row.querySelector("[data-growth-form-target='stageTo']")
      const valueInput = row.querySelector("[data-growth-form-target='stageValue']")
      if (fromInput) fromInput.value = 1
      if (toInput) toInput.value = ""
      if (valueInput) valueInput.value = ""
      this.lockFromFields()
      this.recalculate()
      return
    }

    row.remove()
    this.syncStageChain()
    this.lockFromFields()
    this.recalculate()
  }

  onStageToChange() {
    this.syncStageChain()
    this.recalculate()
  }

  syncStageChain() {
    const rows = this.stageRowTargets
    if (rows.length === 0) return

    rows.forEach((row, index) => {
      const fromInput = row.querySelector("[data-growth-form-target='stageFrom']")
      const toInput = row.querySelector("[data-growth-form-target='stageTo']")
      if (!fromInput || !toInput) return

      if (index === 0) {
        fromInput.value = 1
      } else {
        const prevTo = Number(rows[index - 1].querySelector("[data-growth-form-target='stageTo']")?.value || 0)
        fromInput.value = prevTo > 0 ? prevTo + 1 : ""
      }

      const from = Number(fromInput.value || 0)
      if (from > 0) toInput.min = from
    })
  }

  lockFromFields() {
    this.stageFromTargets.forEach((input) => {
      input.readOnly = true
    })
  }

  lastStageTo() {
    const rows = this.stageRowTargets
    if (rows.length === 0) return 0
    const last = rows[rows.length - 1]
    const to = Number(last.querySelector("[data-growth-form-target='stageTo']")?.value || 0)
    if (to > 0) return to
    return Number(last.querySelector("[data-growth-form-target='stageFrom']")?.value || 0)
  }

  recalculate() {
    const initial = Number(this.initialUsersTarget.value || 0)
    const series = this.projectUsers(initial)
    this.updateSummary(initial, series)
    this.renderChart(series)
  }

  behaviorValue() {
    const checked = this.behaviorTargets.find((el) => el.checked)
    return checked?.value || "exponential"
  }

  modelValue() {
    const checked = this.modelTargets.find((el) => el.checked)
    return checked?.value || "fixed"
  }

  growthValueForMonth(month) {
    if (this.modelValue() === "staged") {
      const stage = this.readStages().find((row) => month >= row.from && month <= row.to)
      return stage ? stage.value : 0
    }
    return Number(this.growthValueTarget.value || 0)
  }

  readStages() {
    return this.stageRowTargets.map((row) => {
      const from = Number(row.querySelector("[data-growth-form-target='stageFrom']")?.value || 0)
      const to = Number(row.querySelector("[data-growth-form-target='stageTo']")?.value || 0)
      const value = Number(row.querySelector("[data-growth-form-target='stageValue']")?.value || 0)
      return { from, to, value }
    }).filter((row) => row.from > 0 && row.to >= row.from)
  }

  acquisitionFor(users, value) {
    if (this.behaviorValue() === "linear") {
      return value
    }
    return users * value / 100
  }

  projectUsers(initial) {
    const series = [Math.round(initial)]
    let users = initial
    for (let month = 1; month <= FORECAST_MONTHS; month += 1) {
      const value = this.growthValueForMonth(month)
      users = users + this.acquisitionFor(users, value)
      series.push(Math.round(users))
    }
    return series
  }

  updateFixedHint() {
    if (!this.hasFixedHintTarget || !this.hasGrowthValueTarget) return
    const value = this.growthValueTarget.value || "0"
    if (this.behaviorValue() === "linear") {
      this.fixedHintTarget.textContent = `Каждый месяц привлекается ${value} новых пользователей.`
    } else {
      this.fixedHintTarget.textContent = `Каждый месяц привлекается ${value}% новых пользователей от текущей базы.`
    }
  }

  updateSummary(initial, series) {
    if (this.hasStartLabelTarget) {
      this.startLabelTarget.textContent = `Старт: ${this.formatNumber(initial)} пользователей`
    }
    if (this.hasMonth12Target) {
      this.month12Target.textContent = this.formatNumber(series[12] || 0)
    }
    if (this.hasMonth24Target) {
      this.month24Target.textContent = this.formatNumber(series[24] || 0)
    }
    this.updateFixedHint()
  }

  renderChart(series) {
    if (!this.hasCanvasTarget) return

    const labels = series.map((_, index) => `Мес ${index}`)
    if (this.chart) {
      this.chart.data.labels = labels
      this.chart.data.datasets[0].data = series
      this.chart.update("none")
      return
    }

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Пользователи",
            data: series,
            borderColor: "#206bc4",
            backgroundColor: "rgba(32, 107, 196, 0.12)",
            fill: true,
            tension: 0.25,
            pointRadius: 0,
            borderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              maxTicksLimit: 8,
              callback(value, index) {
                return index % 4 === 0 ? this.getLabelForValue(value) : ""
              }
            }
          },
          y: {
            beginAtZero: true,
            ticks: { precision: 0 }
          }
        }
      }
    })
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  formatNumber(value) {
    return new Intl.NumberFormat("ru-RU").format(value)
  }
}
